import React, { useState } from 'react';
import { Typography, IconButton, CircularProgress } from '@mui/material';
import { Edit, SettingsOutlined } from '@mui/icons-material';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { WorkHourPrefItemState } from '../../Scheduler';
import { StartEndTimePickerModal } from '../../Scheduler/common';
import { inferEndDayOffset } from '../../../data-access';
import { useGeneralSettings } from '../../../hooks';
import { formatTimeFromHHMM } from '../../../helpers';
import './SchedulerChatUI.scss';
import { useTranslation } from 'react-i18next';

interface SchedulerWorkHoursDisplayProps {
  workHours: WorkHourPrefItemState;
  onWorkHourChange: (pref: WorkHourPrefItemState) => void;
  isLoading?: boolean;
  disabled?: boolean;
  onOpenSettings?: () => void;
}

const SchedulerWorkHoursDisplay: React.FC<SchedulerWorkHoursDisplayProps> = ({
  workHours,
  onWorkHourChange,
  isLoading = false,
  disabled = false,
  onOpenSettings,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  const handleOpenModal = () => {
    setStartTime(dayjs(workHours.start.time, 'HH:mm'));
    setEndTime(dayjs(workHours.end.time, 'HH:mm'));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = (startTime: Dayjs | null, endTime: Dayjs | null) => {
    if (!startTime || !endTime) {
      return;
    }

    const startHHmm = startTime.format('HH:mm');
    const endHHmm = endTime.format('HH:mm');

    onWorkHourChange({
      start: {
        ...workHours.start,
        time: startHHmm,
      },
      end: {
        ...workHours.end,
        time: endHHmm,
      },
      endDayOffset: inferEndDayOffset(startHHmm, endHHmm, workHours.endDayOffset),
    });

    handleCloseModal();
  };

  return (
    <>
      <div className="scheduler-chat-ui__workhours">
        <div className="scheduler-chat-ui__workhours__time_pill">
          {isLoading ? (
            <CircularProgress size={12} />
          ) : (
            <button
              type="button"
              className="scheduler-chat-ui__workhours__button"
              onClick={handleOpenModal}
              disabled={isLoading || disabled}
            >
              <Typography variant="body2" className="scheduler-chat-ui__workhours__text">
                {t('task.scheduler.workHours.workHours')}{' '}
              </Typography>
              <span className="scheduler-chat-ui__workhours__text__time">
                {formatTimeFromHHMM(workHours.start.time, timeFormat)} -{' '}
                {formatTimeFromHHMM(workHours.end.time, timeFormat)}
              </span>
            </button>
          )}

          <IconButton
            size="small"
            onClick={handleOpenModal}
            disabled={isLoading || disabled}
            className="scheduler-chat-ui__workhours__edit"
            aria-label={t('task.scheduler.workHours.editWorkHours')}
          >
            <Edit fontSize="small" />
          </IconButton>
        </div>

        <IconButton
          size="small"
          onClick={onOpenSettings}
          disabled={!onOpenSettings}
          className="scheduler-chat-ui__workhours__settings_pill"
          aria-label={t('task.sidebar.openAiAssistantSettings')}
        >
          <SettingsOutlined fontSize="small" />
        </IconButton>
      </div>
      <StartEndTimePickerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        initialStartTime={startTime}
        initialEndTime={endTime}
        timeFormat={timeFormat}
        title={t('task.scheduler.workHours.setWorkHours')}
      />
    </>
  );
};

export default SchedulerWorkHoursDisplay;
